import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Briefcase, Car, Shield, Calculator, HelpCircle, Phone, Mail } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { useLanguage } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";
import { Link } from "react-router-dom";

export default function FaqPage() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // FAQ data with translations
  const companyFaqs = [
    {
      question: getTranslation("faqCompany1Question", language),
      answer: getTranslation("faqCompany1Answer", language)
    },
    {
      question: getTranslation("faqCompany2Question", language),
      answer: getTranslation("faqCompany2Answer", language)
    },
    {
      question: getTranslation("faqCompany3Question", language),
      answer: getTranslation("faqCompany3Answer", language)
    },
    {
      question: getTranslation("faqCompany4Question", language),
      answer: getTranslation("faqCompany4Answer", language)
    },
    {
      question: getTranslation("faqCompany5Question", language),
      answer: getTranslation("faqCompany5Answer", language)
    }
  ];

  const autoFaqs = [
    {
      question: getTranslation("faqAuto1Question", language),
      answer: getTranslation("faqAuto1Answer", language)
    },
    {
      question: getTranslation("faqAuto2Question", language),
      answer: getTranslation("faqAuto2Answer", language)
    },
    {
      question: getTranslation("faqAuto3Question", language),
      answer: getTranslation("faqAuto3Answer", language)
    },
    {
      question: getTranslation("faqAuto4Question", language),
      answer: getTranslation("faqAuto4Answer", language)
    }
  ];

  const supportFaqs = [
    {
      question: getTranslation("faqSupport1Question", language),
      answer: getTranslation("faqSupport1Answer", language)
    },
    {
      question: getTranslation("faqSupport2Question", language),
      answer: getTranslation("faqSupport2Answer", language)
    },
    {
      question: getTranslation("faqSupport3Question", language),
      answer: getTranslation("faqSupport3Answer", language)
    },
    {
      question: getTranslation("faqSupport4Question", language),
      answer: getTranslation("faqSupport4Answer", language)
    }
  ];

  const taxFaqs = [
    {
      question: getTranslation("faqTax1Question", language),
      answer: getTranslation("faqTax1Answer", language)
    },
    {
      question: getTranslation("faqTax2Question", language),
      answer: getTranslation("faqTax2Answer", language)
    },
    {
      question: getTranslation("faqTax3Question", language),
      answer: getTranslation("faqTax3Answer", language)
    },
    {
      question: getTranslation("faqTax4Question", language),
      answer: getTranslation("faqTax4Answer", language)
    }
  ];
  
  const allFaqs = [...companyFaqs, ...autoFaqs, ...supportFaqs, ...taxFaqs];
  
  const filteredFaqs = (faqs: typeof companyFaqs) => {
    if (!searchQuery) return faqs;
    
    return faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-muted/30 py-12 md:py-16">
        <div className="container">
          <BackButton className="mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{getTranslation("faqTitle", language)}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {getTranslation("faqDescription", language)}
          </p>
          
          {/* Search Box */}
          <div className="mt-6 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={getTranslation("faqSearch", language)}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ Content */}
      <div className="container py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              <span>{getTranslation("faqTabAll", language)}</span>
            </TabsTrigger>
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span>{getTranslation("faqTabCompany", language)}</span>
            </TabsTrigger>
            <TabsTrigger value="auto" className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              <span>{getTranslation("faqTabAuto", language)}</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>{getTranslation("faqTabSupport", language)}</span>
            </TabsTrigger>
            <TabsTrigger value="tax" className="flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <span>{getTranslation("faqTabTax", language)}</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-8">
            <TabsContent value="all">
              <h2 className="text-2xl font-semibold mb-6">{getTranslation("faqAllQuestions", language)}</h2>
              {filteredFaqs(allFaqs).length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFaqs(allFaqs).map((faq, index) => (
                    <AccordionItem key={index} value={`all-item-${index}`}>
                      <AccordionTrigger className="text-lg font-medium text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{getTranslation("faqNoResults", language)} "{searchQuery}"</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="company">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Briefcase className="h-6 w-6 text-primary" />
                {getTranslation("faqCompanyServices", language)}
              </h2>
              {filteredFaqs(companyFaqs).length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFaqs(companyFaqs).map((faq, index) => (
                    <AccordionItem key={index} value={`company-item-${index}`}>
                      <AccordionTrigger className="text-lg font-medium text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{getTranslation("faqNoResults", language)} "{searchQuery}"</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="auto">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Car className="h-6 w-6 text-primary" />
                {getTranslation("faqAutoServices", language)}
              </h2>
              {filteredFaqs(autoFaqs).length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFaqs(autoFaqs).map((faq, index) => (
                    <AccordionItem key={index} value={`auto-item-${index}`}>
                      <AccordionTrigger className="text-lg font-medium text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{getTranslation("faqNoResults", language)} "{searchQuery}"</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="support">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                {getTranslation("faqLegalSupport", language)}
              </h2>
              {filteredFaqs(supportFaqs).length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFaqs(supportFaqs).map((faq, index) => (
                    <AccordionItem key={index} value={`support-item-${index}`}>
                      <AccordionTrigger className="text-lg font-medium text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{getTranslation("faqNoResults", language)} "{searchQuery}"</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="tax">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Calculator className="h-6 w-6 text-primary" />
                {getTranslation("faqAccountingServices", language)}
              </h2>
              {filteredFaqs(taxFaqs).length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFaqs(taxFaqs).map((faq, index) => (
                    <AccordionItem key={index} value={`tax-item-${index}`}>
                      <AccordionTrigger className="text-lg font-medium text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{getTranslation("faqNoResults", language)} "{searchQuery}"</p>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Contact Buttons instead of Ask Question Form */}
        <div className="mt-16 py-10 px-6 bg-muted/30 rounded-xl text-center">
          <h3 className="text-2xl font-semibold mb-4">{getTranslation("faqAskQuestion", language)}</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            {getTranslation("faqAskDescription", language)}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="flex items-center gap-2">
              <a href="tel:+48886695593">
                <Phone className="h-5 w-5" />
                {getTranslation("quickContact", language)}
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="flex items-center gap-2">
              <a href="mailto:kontakt1group.pl@gmail.com">
                <Mail className="h-5 w-5" />
                {getTranslation("emailContact", language)}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}